import type {
	BackgroundGeolocationPlugin,
	Location as ReceivedLocation,
	CallbackError,
	WatcherOptions
} from '@capacitor-community/background-geolocation';

import { driver_state } from '$lib/features/profile/states/driver_state';
import { permissions_state } from '$lib/utils/states/permissions_state';
import { LocalNotifications } from '@capacitor/local-notifications';
import { routes_state } from '../../routes/states/routes_state';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { cap_utils } from '$lib/utils/cap_utils';
import { CapacitorHttp } from '@capacitor/core';
import { utils } from '$lib/utils/utils.svelte';
import { Loc } from '$lib/tools/locale.svelte';
import { auth_sdk } from '$lib/tools/graphdb';

type CapturingStatus = 'capturing' | 'stopped' | 'initializing';

export let location_capturing_timeout_modal = $state<{ end_stamp?: number; show: boolean; force_exit?: boolean }>({
	show: false
});
// const LOCATION_CAPTURE_TIMEOUT = (+import.meta.env.VITE_LOCATION_CAPTURE_TIMEOUT_SEC || 60) * 1000

import { WATCHER_ID_STORAGE_KEY, CAPTURING_STATUS_STORAGE_KEY, LATEST_LOCATION_STORAGE_KEY } from './geo_storage_keys';

const watcher_id_state = utils.states.create_persisted_string<string>({
	initial_value: '',
	key: WATCHER_ID_STORAGE_KEY
});
const capturing_status_state = utils.states.create_persisted_string<CapturingStatus>({
	initial_value: 'stopped',
	key: CAPTURING_STATUS_STORAGE_KEY
});
const latest_location_state = utils.states.create_persisted<ReceivedLocation | null>({
	initial_value: null,
	key: LATEST_LOCATION_STORAGE_KEY
});

export function reset_persisted_geo_state() {
	watcher_id_state.value = '';
	capturing_status_state.value = 'stopped';
	latest_location_state.value = null;
}

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');

function create_geo_state() {
	const handlers = {
		async watch_position(): Promise<boolean> {
			console.log('watch_position');

			try {
				if (!utils.is_browser) return true;
				if (capturing_status_state.value === 'initializing') throw 'is executing';

				const location_permission = permissions_state.value.location_permission;
				if (!location_permission.is_granted) {
					const success = await handlers.launch_location_permission_modal();
					if (!success) throw 'location_permission_modal_closed_with_no_permission_granted';
				}

				if (!cap_utils.device.is_device_native()) return true;

				await handlers.clear_watch_position();
				capturing_status_state.value = 'initializing';

				const options: WatcherOptions = {
					backgroundMessage: Loc('press_to_cancel'),
					backgroundTitle: Loc('tracking_your'),
					requestPermissions: true,
					stale: false,
					distanceFilter: 5
				};

				async function location_callback_fn(location?: ReceivedLocation, error?: CallbackError) {
					if (error) {
						console.error('error with location', { error });

						if (error.code === 'NOT_AUTHORIZED') {
							if (window.confirm(`${Loc('location_services_are_off')}\n\n ${Loc('open_settings')}`)) {
								BackgroundGeolocation.openSettings();
							}
						}
						return false;
					}

					if (!location) return false;

					capturing_status_state.value = 'capturing';
					latest_location_state.value = location;
					await location_update_db({ location });
				}
				const watcher_id = await BackgroundGeolocation.addWatcher(options, location_callback_fn);

				if (!watcher_id) throw new Error('no watcher id #$');

				watcher_id_state.value = watcher_id;

				//
				return true;
			} catch (error) {
				await handlers.clear_watch_position();
				console.error('error at watch_position fn:', error);
				return false;
			}
		},

		async clear_watch_position(): Promise<boolean> {
			console.log('clear_watch_position');
			try {
				if (!utils.is_browser) return true;

				const store_value = watcher_id_state.value;
				if (!store_value) return true;

				await BackgroundGeolocation.removeWatcher({ id: store_value });
				console.log('finished clear watch position');
				return true;
			} catch (error) {
				console.error('error at clear_watch_position: ', error);
				return false;
			} finally {
				watcher_id_state.value = '';
				capturing_status_state.value = 'stopped';
			}
		},

		async get_initial_location({ timeout }: { timeout: number }): Promise<ReceivedLocation | null> {
			console.log('get_initial_location');
			try {
				let received_location: ReceivedLocation | undefined;

				const options: WatcherOptions = {
					backgroundMessage: Loc('press_to_cancel'),
					backgroundTitle: Loc('tracking_your'),
					requestPermissions: true,
					stale: true
				};

				await handlers.clear_watch_position();

				async function location_callback_fn(location?: ReceivedLocation, error?: CallbackError) {
					if (error) {
						console.error('error with get_initial_location location', { error });

						if (error.code === 'NOT_AUTHORIZED') {
							if (window.confirm(`${Loc('no_location_permission')}, \n\n ${Loc('open_settings')}`)) {
								BackgroundGeolocation.openSettings();
							}
						}
						return false;
					}

					if (!location) return false;

					console.error('location received');
					received_location = location;
					return true;
				}

				const watcher_id = await BackgroundGeolocation.addWatcher(options, location_callback_fn);

				await new Promise((resolve) => {
					setTimeout(resolve, timeout);
				});

				await BackgroundGeolocation.removeWatcher({ id: watcher_id });

				if (!received_location?.latitude) return null;
				return received_location;
			} catch (error) {
				console.error(error);
				return null;
			}
		},

		async get_location_permission(delay = 4000): Promise<boolean> {
			console.log('get_location_permission');
			try {
				if (!cap_utils.device.is_device_native() || !utils.is_browser) {
					permissions_state.update_location_permission(true);
					return true;
				}

				const local_notifications_permission = await LocalNotifications.checkPermissions();
				if (local_notifications_permission.display !== 'granted') {
					const request_local_notifications_permission = await LocalNotifications.requestPermissions();
					if (request_local_notifications_permission.display !== 'granted')
						throw 'local notifications permission not granted';
				}

				const success = await handlers.get_initial_location({ timeout: delay });

				if (!success) return false;

				permissions_state.update_location_permission(true);

				return true;
			} catch (error) {
				console.error('error at get_location_permission: ', error);
				return false;
			}
		},

		async launch_location_permission_modal(): Promise<boolean> {
			console.log('launch_location_permission_modal');
			try {
				const { promise, resolve } = Promise.withResolvers<boolean>();

				const [{ root_modal_state }, LocationPermissionModule] = await Promise.all([
					import('$lib/utils/states/root_modal_state.svelte'),
					import('$lib/features/geo/components/LocationPermission.svelte')
				]);

				async function onmodal_close() {
					const has_location_permission = permissions_state.value.location_permission.is_granted;
					if (has_location_permission) resolve(true);
					else resolve(false);
				}

				root_modal_state.open({
					component: LocationPermissionModule.default,
					props: { close_modal: root_modal_state.close },
					modal_props: { title: 'location_permission', onclose: onmodal_close }
				});

				return promise;
			} catch (error) {
				console.error('error at launch_location_permission_modal:', { error });
				return false;
			}
		}
	};

	return {
		get is_location_capturing() {
			return capturing_status_state.value === 'capturing';
		},
		get watcher_id_state() {
			return watcher_id_state.value;
		},
		get latest_location() {
			return latest_location_state.value;
		},

		...handlers
	};
}

export async function location_update_db({ location }: { location: ReceivedLocation }) {
	try {
		if (!location) return { success: false, location: null };

		const route_id = routes_state.assigned.assigned_route_id.value || null;
		const driver_id = driver_state.value?.id;
		if (!driver_id) throw new Error('no_driver_id');

		const location_geojson = utils.addresses.create_address_object(location);

		const query = `mutation($object:drivers_locations_insert_input!){insert_drivers_locations_one(object:$object){id}}`;

		const variables = {
			object: {
				altitude_accuracy: location.altitudeAccuracy ?? null,
				bearing_string: location.bearing ? location.bearing.toString() : '',
				location_accuracy_radius: location.accuracy,
				location_recorded_time_unix: location.time,
				altitude: location.altitude ?? null,
				speed: location.speed ?? null,
				simulated: location.simulated,
				location: location_geojson,
				driver_id,
				route_id
			}
		};

		const access_token = await auth_sdk.get_access_token();
		if (!access_token) throw new Error('no_access_token');

		const insert_op = await CapacitorHttp.post({
			url: import.meta.env.VITE_GRAPHQL_ENDPOINT,
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${access_token}`
			},
			data: JSON.stringify({ query, variables })
		});
		console.log({ insert_op });

		if (insert_op.data.errors?.length) {
			console.error(insert_op.data);
			throw new Error(insert_op.data.errors[0].message);
		}

		if (!insert_op.data?.insert_drivers_locations_one?.id) {
			console.error(insert_op.data);
			throw new Error('no data');
		}

		return true;
	} catch (error) {
		console.error('error at location_update_db.ts function:', error);
		return false;
	}
}

export const geo_state = create_geo_state();
